import { useState } from 'react';
import { t } from '@services/i18n';
import { RwLoading } from '@components/RwLoading';
import { useApiQuery } from '@hooks/useApiQuery';
import { RwEmpty } from '@components/RwEmpty';
import { MyLedger } from './MyLedger';
import { AiMyBalance } from '../components/AiMyBalance';
import type { Me, MyLedgerEntry } from '@shared/types/me';
import type { MemberScreenProps } from './types';

/**
 * "My rewards" — a read-only view of the member's personal ledger: every point
 * earned and why, plus when each batch expires. A segmented all/points/
 * reputation filter (real local state) narrows the feed; three wallet cards
 * summarise standing. Renders only real data from the `transactions` endpoint.
 */

type CurrencyFilter = 'all' | 'points' | 'reputation';

interface ActivityResponse {
  me: Me;
  rows: MyLedgerEntry[];
  total: number;
}

/** Empty, fabrication-free Me used only by the standalone Vite harness fallback. */
const emptyMe: Me = {
  id: 0,
  name: '',
  handle: '',
  avatar: '',
  points: 0,
  rep: 0,
  rank: 0,
  totalUsers: 0,
  joined: '',
  level: '',
  nextLevel: null,
  streak: 0,
  longestStreak: 0,
  pointsExpiring: 0,
  expiringOn: '',
  nextBadge: { name: '', progress: 0, remaining: 0 },
};

export function UserActivity(_props: MemberScreenProps) {
  const [cur, setCur] = useState<CurrencyFilter>('all');
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const { data, isLoading } = useApiQuery<ActivityResponse>(
    ['rw-activity', cur, page],
    'transactions',
    {
      params: { limit: pageSize, offset: page * pageSize, currency: cur === 'all' ? '' : cur },
      fallback: { me: emptyMe, rows: [], total: 0 },
    },
  );
  const me = data?.me ?? emptyMe;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  if (isLoading) return <RwLoading variant="table" />;

  const setFilter = (next: CurrencyFilter): void => {
    setCur(next);
    setPage(0);
  };
  const fromRow = total === 0 ? 0 : page * pageSize + 1;
  const toRow = Math.min(total, (page + 1) * pageSize);

  return (
    <div className="rw-page rw-user">
      <header className="rw-page-h">
        <div>
          <div className="rw-eyebrow">{t('COM_REWARDIFY_UACT_HISTORY', 'History')}</div>
          <h1 className="rw-h1">{t('COM_REWARDIFY_UACT_MY_REWARDS', 'My rewards')}</h1>
          <p className="rw-lede">
            {t(
              'COM_REWARDIFY_UACT_LEDE',
              "Every point you've earned and why, plus when each batch expires. This is a read-only view of your ledger.",
            )}
          </p>
        </div>
        <div className="rw-toolbar">
          <div className="rw-segmented">
            <button type="button" className={cur === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>
              {t('COM_REWARDIFY_UACT_FILTER_ALL', 'All')}
            </button>
            <button type="button" className={cur === 'points' ? 'on' : ''} onClick={() => setFilter('points')}>
              {t('COM_REWARDIFY_UACT_FILTER_POINTS', 'Points')}
            </button>
            <button type="button" className={cur === 'reputation' ? 'on' : ''} onClick={() => setFilter('reputation')}>
              {t('COM_REWARDIFY_UACT_FILTER_REPUTATION', 'Reputation')}
            </button>
          </div>
        </div>
      </header>

      <div className="rw-wallet">
        <div className="rw-wallet-card">
          <div className="rw-wallet-l">{t('COM_REWARDIFY_UACT_COMMUNITY_POINTS', 'Community Points')}</div>
          <div className="rw-wallet-v" style={{ color: 'var(--rw-accent-fg)', fontSize: 32 }}>
            {me.points.toLocaleString()}
          </div>
          <div className="rw-wallet-s">
            {t('COM_REWARDIFY_UACT_EXPIRE', '%s expire %s', me.pointsExpiring, me.expiringOn)}
          </div>
        </div>
        <div className="rw-wallet-card">
          <div className="rw-wallet-l">{t('COM_REWARDIFY_UACT_REPUTATION', 'Reputation')}</div>
          <div className="rw-wallet-v" style={{ fontSize: 32 }}>
            {me.rep.toLocaleString()}
          </div>
          <div className="rw-wallet-s">{t('COM_REWARDIFY_UACT_LIFETIME_LEVEL', 'lifetime · drives your level')}</div>
        </div>
        <div className="rw-wallet-card">
          <div className="rw-wallet-l">{t('COM_REWARDIFY_UACT_REDEEMED', 'Redeemed (all time)')}</div>
          <div className="rw-wallet-v" style={{ fontSize: 32 }}>
            {'—'}
          </div>
          <div className="rw-wallet-s">{t('COM_REWARDIFY_UACT_ACROSS_REDEMPTIONS', 'across your redemptions')}</div>
        </div>
      </div>

      {/* Renders nothing unless the site switched the member explainer on. */}
      <AiMyBalance currency={cur === 'all' ? '' : cur} />

      <section className="rw-card">
        {total === 0 ? (
          <RwEmpty
            title={t('COM_REWARDIFY_UACT_EMPTY', 'No activity yet')}
            hint={t('COM_REWARDIFY_UACT_EMPTY_HINT', 'Points you earn will appear here.')}
          />
        ) : (
          <>
            <MyLedger rows={rows} />
            {total > pageSize && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
                <span className="rw-fg2" style={{ fontSize: 12 }}>
                  {t('COM_REWARDIFY_PAGER_RANGE', '%s–%s of %s', fromRow, toRow, total)}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="rw-btn rw-btn-ghost rw-btn-sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                    {t('COM_REWARDIFY_PAGER_PREV', 'Previous')}
                  </button>
                  <button type="button" className="rw-btn rw-btn-ghost rw-btn-sm" disabled={(page + 1) * pageSize >= total} onClick={() => setPage((p) => p + 1)}>
                    {t('COM_REWARDIFY_PAGER_NEXT', 'Next')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
